import { FC, useState } from 'react';
import {
  FAIcon,
  useTranslation,
  TextLink,
  TextLinkStyle,
  Button,
  ButtonStyles,
  Carousel,
  useBreakpoint,
  Breakpoints,
  Drawer,
  Modal,
  TextAlign,
} from '@nextgen-web-framework/all';
// eslint-disable-next-line unused-imports/no-unused-imports
import type { IPromiseData, PromiseComponentPropsTypes } from './types/propsTypes';
import styles from './PromiseComponent.module.scss';

const PromiseComponent: FC<PromiseComponentPropsTypes> = ({
  promiseDetails,
}: PromiseComponentPropsTypes): JSX.Element => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const promisesData = promiseDetails?.data;
  const t = useTranslation();
  let isMobile = false;
  const viewportType = useBreakpoint();
  if ([Breakpoints.XS, Breakpoints.SM].includes(viewportType as Breakpoints)) {
    isMobile = true;
  }

  const openDetails = () => setShowDetails(true);

  const closeDetails = () => setShowDetails(false);

  const learnMoreButtonStyle: React.CSSProperties = { backgroundColor: '#f0fae5', marginTop: 16 };

  const PromiseCard: FC<{ promiseData: IPromiseData }> = ({ promiseData }) => (
    <div className={styles['promise-card']}>
      <div className={styles['promise-card-icon-container']}>
        <FAIcon iconName={promiseData.icon} />
      </div>
      <div className={styles['promise-details']}>
        <div className={styles['promise-details-title']}>{promiseData.title}</div>
        <div className={styles['promise-description']}>
          {promiseData.description}
          {!!promiseData.url && (
            <div className={styles['promise-view-more-link']}>
              {'… '}
              <TextLink
                text={promiseData.linkText || t('product.promiseComponent.viewMore')}
                textLinkStyle={TextLinkStyle.UNDERLINE}
                href={promiseData.url}
                target="_blank"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const promiseCards = promisesData?.length > 0 && (
    <div className={styles['promise-cards-container']}>
      {promisesData.map((promiseData, index) => (
        <PromiseCard key={`promise-detail#${index + 1}`} promiseData={promiseData} />
      ))}
    </div>
  );

  return (
    <>
      {promisesData?.length > 0 && (
        <>
          <div className={`${styles['cpw-promises-container']}`}>
            <div className={`${styles['cpw-promises-data-container']}`} data-testid={'cpw-promises-container-testid'}>
              <div className={`${styles['promises-title-container']}`}>
                <h4 className={`${styles['promises-title']}`} data-testid={'promises-title-testid'}>
                  {promiseDetails.title || t('product.promiseComponent.header')}
                </h4>
                {!isMobile && (
                  <div className={styles['promises-learn-more-link']}>
                    <TextLink
                      textLinkStyle={TextLinkStyle.UNDERLINE}
                      href={''}
                      onClick={openDetails}
                      text={t('product.common.learnMore')}
                    />
                  </div>
                )}
              </div>
              {promisesData?.length > 0 && (
                <Carousel isTileCarousel data-testid={'promises-testid'}>
                  {promisesData.map((promise, index) => (
                    <div key={`promise#${index + 1}`} className={`${styles['promise-container']}`}>
                      <div className={styles['promise-icon-container']}>
                        <FAIcon iconName={promise.icon} />
                      </div>
                      <span className={styles['promise-title']}>{promise.title}</span>
                    </div>
                  ))}
                </Carousel>
              )}
              {isMobile && (
                <Button
                  style={learnMoreButtonStyle}
                  title={t('product.common.learnMore')}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={openDetails}
                />
              )}
            </div>
            {isMobile && showDetails && (
              <Drawer
                header={promiseDetails.title || t('product.promiseComponent.header')}
                closeOnBackdropClick={true}
                onClose={closeDetails}
                closeIcon={true}
                bottomGap={0}
                footer={<Button title={t('product.common.done')} onClick={closeDetails} />}
              >
                {promiseCards}
              </Drawer>
            )}
            {!isMobile && showDetails && (
              <Modal
                headline={promiseDetails.title || t('product.promiseComponent.header')}
                primaryButtonLabel={t('product.common.done')}
                onPrimaryButtonClick={closeDetails}
                closeOnBackdropClick={true}
                closeIcon={true}
                onClose={closeDetails}
                textAlign={TextAlign.CENTER}
                className={styles['ingredients-modal-width']}
              >
                <div>{promiseCards}</div>
              </Modal>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default PromiseComponent;
export type { PromiseComponentPropsTypes };
